"use client";

import { useCallback, useRef } from "react";
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { useLocale, useTranslations } from "next-intl";
import { RetrievedHistoryItem } from "@/lib/sanity/responseTypes";

import ChevronLeft from "@/components/icons/chevron-left.svg";
import ChevronRight from "@/components/icons/chevron-right.svg";

interface HistoryProps {
  history: RetrievedHistoryItem[];
}

export default function History(props: HistoryProps) {
  const t = useTranslations('About');
  const locale = useLocale();

  const { history } = props;

  const sliderRef = useRef<SwiperRef>(null);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  return (
    <div className="flex flex-col w-full pt-20 px-1 md:px-2.5 gap-2 md:gap-[30px] text-primary font-[Outfit]">
      <div className="font-bold text-5xl leading-[120%] w-full text-center">
        {t('history_title')}
      </div>
      <div className="w-full px-1 md:px-5 flex flex-row gap-5">
        <div className="min-h-auto flex flex-col justify-center">
          <button className="btn rounded-full" onClick={handlePrev}>
            <ChevronLeft />
          </button>
        </div>
        <Swiper
          ref={sliderRef}
          modules={[Navigation, Pagination]}
          pagination={{ clickable: true }}
          spaceBetween={20}
          slidesPerView={3}
          centeredSlides={false}
          grabCursor={true}
          loop={false}
          slideToClickedSlide={true}
          style={{ padding: '2rem 0' }}
          breakpoints={{
            300: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
        >
          {history.map((item, index) => (
            <SwiperSlide key={`history-item-${index}`}>
              <div className="p-4 h-full flex flex-col gap-2.5">
                <div className="font-bold text-xl md:text-2xl leading-[120%] w-full text-center">{item.when[locale]} - {item.what[locale]}</div>
                <div className="font-medium text-lg md:text-xl leading-[120%] w-full text-center">{item.description[locale]}</div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="min-h-auto flex flex-col justify-center">
          <button className="btn rounded-full" onClick={handleNext}>
            <ChevronRight />
          </button>
        </div>
      </div>
    </div>
  )
}
